/* eslint-disable no-underscore-dangle -- `_hsq` is HubSpot's global queue name. */

import type { AnalyticsEventName } from './events';
import { isHubSpotEnabled, isHubSpotLoaded } from './hubspot';
import type { AnalyticsSink } from './sinks';

/**
 * The HubSpot destination for `track()`.
 *
 * Currently a no-op for every event, on purpose — and that is a tier fact, not
 * an oversight. Custom behavioural events require Marketing Hub Enterprise; on
 * this account `_hsq.push(['trackCustomBehavioralEvent', …])` is accepted and
 * then dropped server-side, so mapping events today would produce code that
 * looks like it works, reports no errors, and sends nothing.
 *
 * What HubSpot *does* receive is pageviews (./HubSpotPageViews) and, for
 * already-known contacts, an identity association (./AnalyticsBootstrap). This
 * sink exists so that the day the account is upgraded, wiring events up is a map
 * entry rather than an architecture change.
 */

type HubSpotMapping =
  | { kind: 'ignore' }
  | { kind: 'behavioral'; eventName: string };

/**
 * Flip to `true` only after the HubSpot account reaches Marketing Hub
 * Enterprise. Not an env var: this describes what the CRM account is capable of,
 * which is not something a deploy target gets to disagree about.
 */
const HUBSPOT_BEHAVIORAL_EVENTS_ENABLED = false;

/**
 * Partial by design — an unmapped event is a silent no-op, so adding events to
 * ./events.ts never forces a change here. That is the right default when ~58 of
 * the ~60 portal events (report downloads, brand switches, wizard steps) have no
 * meaning to a marketing CRM.
 *
 * Anything added here must be a genuine marketing signal. In particular, do NOT
 * map `auth_login_completed` or `auth_session_restored`: see the cost note in
 * ./hubspot.ts.
 */
const EVENT_MAP: Partial<Record<AnalyticsEventName, HubSpotMapping>> = {};

export const hubspotSink: AnalyticsSink = {
  name: 'hubspot',

  // The script's presence *is* the consent gate: it is only ever injected for
  // an accept-all decision (see applyConsentToHubSpot), and removed on
  // withdrawal. Checking it per event means a mid-session withdrawal takes
  // effect on the very next `track()` call.
  isEnabled: () => isHubSpotEnabled && isHubSpotLoaded(),

  capture: (event) => {
    const mapping = EVENT_MAP[event];
    if (!mapping || mapping.kind === 'ignore') return;

    if (mapping.kind === 'behavioral') {
      if (!HUBSPOT_BEHAVIORAL_EVENTS_ENABLED) return;
      window._hsq?.push([
        'trackCustomBehavioralEvent',
        { name: mapping.eventName }
      ]);
    }
  }
};
